import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"
import { motion } from "framer-motion"

import { cn } from "@/lib/utils"

const Tabs = TabsPrimitive.Root

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      "inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground",
      className
    )}
    {...props}
  />
))
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      "inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm",
      className
    )}
    {...props}
  />
))
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      // An INACTIVE panel must never occupy layout space.
      //
      // Radix passes its children as a FUNCTION, and `Presence` treats a
      // function child as force-mounted (`const forceMount = typeof children
      // === "function"`), so the inactive panel's <div> STAYS IN THE DOM with
      // `hidden` set and no children. `hidden` only hides it via the UA
      // stylesheet's `[hidden] { display: none }` — which any author-level
      // display utility outranks. So a panel classed `flex`/`grid` renders as
      // an empty box, and with `flex-1` it eats every remaining pixel of its
      // parent, pushing the ACTIVE panel down the page.
      //
      // That is exactly what happened on /home/data-stores: the "My Data
      // Stores" panel (`mt-0 flex flex-1 flex-col`, declared before the
      // Library panel) stayed mounted while the Library tab was open and
      // opened a ~500px blank gap above the Library card.
      //
      // The `data-[state=inactive]:` variant compiles to
      // `.…\:hidden[data-state="inactive"]` — specificity 0,2,0 — so it beats
      // a plain `.flex`/`.grid` (0,1,0) no matter what a caller passes in.
      // Specificity, not class order, is what decides this.
      "data-[state=inactive]:hidden",
      className
    )}
    {...props}
  />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
